import { TYPE, type KeyRect, type Rect } from "@gaoya/shared";
import { C, drawBackspaceIcon, ENAMEL, enamelLabel, fillEnamel, fillRound, fillRoundTop } from "../theme.js";
import { battleKeys } from "../../ui/keyboard.js";
import { isMyTurn, remainingMs, type UiState } from "../../game/state.js";

export function waitingOpponentLabel(ui: UiState, now: number): string {
  if (!ui.connected) return "茶线断了，正在重连…";
  if (ui.mode === "RUSH" && !ui.selfRacing) return "你已离席，本局观战";
  if (ui.deadline <= 0) return ui.mode === "RUSH" ? "即将开抢" : "即将开席";
  if (remainingMs(ui, now) <= 0) return "对方超时，正在结算…";
  if (ui.mode !== "RUSH" && (ui.oppScramble?.turnsLeft ?? 0) > 0) return "对方活字已乱";
  return ui.mode === "RUSH" ? "开抢中" : "对方码字中";
}

export function drawKeyboardSkin(
  ctx: CanvasRenderingContext2D,
  ui: UiState,
  tray: Rect,
  keysArea: Rect,
  now: number,
): void {
  fillRoundTop(ctx, tray.x, tray.y, tray.w, tray.h + 2, 22, C.night);
  ctx.fillStyle = "rgba(255,240,220,0.10)";
  ctx.fillRect(tray.x + 22, tray.y, tray.w - 44, 1);
  fillRound(ctx, tray.x + tray.w / 2 - 18, tray.y + 6, 36, 3, 1.5, "rgba(255,240,220,0.14)");
  const enabled = isMyTurn(ui);
  const scrambled = (ui.selfScramble?.turnsLeft ?? 0) > 0;
  const keys = battleKeys(ui, keysArea);
  for (const k of keys) drawKey(ctx, k, ui.pressedKey === String(k.id), enabled, scrambled);
  if (enabled && scrambled) {
    const turns = ui.selfScramble?.turnsLeft ?? 0;
    const label = `活字已乱 · 还剩 ${turns} 手`;
    ctx.font = `600 12px ${TYPE.sans}`;
    const tw = Math.min(keysArea.w - 24, ctx.measureText(label).width + 24);
    const lx = keysArea.x + (keysArea.w - tw) / 2;
    fillRound(ctx, lx, tray.y + 4, tw, 18, 9, "rgba(200, 60, 35, 0.92)");
    ctx.fillStyle = "#FFF7EE";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, keysArea.x + keysArea.w / 2, tray.y + 13);
  }
  if (!enabled) {
    fillRound(ctx, keysArea.x, keysArea.y, keysArea.w, keysArea.h, 12, "rgba(42,36,32,0.45)");
    const label = waitingOpponentLabel(ui, now);
    ctx.font = `600 15px ${TYPE.sans}`;
    const padL = 42;
    const padR = 16;
    const textW = ctx.measureText(label).width;
    const tw = Math.min(keysArea.w - 16, Math.max(140, padL + textW + padR));
    const th = 32;
    const lx = keysArea.x + (keysArea.w - tw) / 2;
    const ly = keysArea.y + keysArea.h * 0.36 - th / 2;
    fillRound(ctx, lx, ly, tw, th, 16, "rgba(255,252,245,0.97)");
    const dotT = Math.floor(now / 350) % 3;
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = i === dotT ? C.seal : "rgba(184,58,36,0.3)";
      ctx.beginPath();
      ctx.arc(lx + 16 + i * 7, ly + th / 2, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = C.ink;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, lx + padL, ly + th / 2 + 0.5);
  }
  if (ui.popup && enabled) {
    const p = ui.popup;
    fillRound(ctx, p.x - 24, p.y - 56, 48, 46, 12, C.card);
    ctx.fillStyle = C.seal;
    ctx.font = `700 24px ${TYPE.sans}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(p.label, p.x, p.y - 33);
  }
}

function drawKey(
  ctx: CanvasRenderingContext2D,
  k: KeyRect,
  pressed: boolean,
  enabled: boolean,
  scrambled = false,
): void {
  const pad = k.visualPad;
  const x = k.x + pad;
  const y = k.y + pad;
  const w = k.w - pad * 2;
  const h = k.h - pad * 2;
  const special = k.id === "SUBMIT" || k.id === "BACKSPACE";
  const r = special ? 10 : 8;
  const tone = !enabled
    ? ENAMEL.inkstone
    : k.id === "SUBMIT"
      ? ENAMEL.rouge
      : k.id === "BACKSPACE"
        ? ENAMEL.inkstone
        : scrambled
          ? ENAMEL.gamboge
          : ENAMEL.porcelain;
  const face = fillEnamel(ctx, x, y, w, h, r, tone, pressed && enabled);
  const ink = enabled ? tone.ink : "rgba(244,237,226,0.45)";
  if (k.id === "BACKSPACE") {
    drawBackspaceIcon(ctx, face.x + face.w / 2, face.y + face.h / 2, Math.min(20, face.w * 0.42), ink);
    return;
  }
  const fs = k.id === "SUBMIT" ? 14 : 17;
  ctx.font = `${k.id === "SUBMIT" ? 700 : 600} ${fs}px ${TYPE.sans}`;
  enamelLabel(ctx, k.label, face.x + face.w / 2, face.y + face.h / 2, ink);
}
